// BYOKPromoBanner — dashboard-level nudge.
//
// Feb 27, 2026. Shows on the creator dashboard ONLY when the creator hasn't
// yet brought their own Anthropic or OpenAI key. Pitches the savings (80%
// off Claude / GPT) and links to the BYOK panel below. Dismissible per
// session via localStorage so it stops nagging users who've decided to
// keep using the platform reservoir for now.

import { useState } from "react";
import { Sparkle, X, ArrowRight } from "@phosphor-icons/react";

const DISMISS_KEY = "mysilo.byok_promo_dismissed_v1";

export default function BYOKPromoBanner({ user }) {
  const [dismissed, setDismissed] = useState(() => {
    try { return !!localStorage.getItem(DISMISS_KEY); } catch { return false; }
  });

  // Only show when the user has zero direct provider keys for non-Gemini
  // providers. The moment they save Anthropic OR OpenAI, hide the banner —
  // they've made the cost-aware decision and don't need the nudge anymore.
  const hasAny = !!user?.has_anthropic_byok || !!user?.has_openai_byok;
  if (hasAny || dismissed) return null;

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* noop */ }
    setDismissed(true);
  };

  const scrollToBYOK = () => {
    const el = document.getElementById("creator-byok-providers-card");
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      // Brief highlight flash so users find the panel after the scroll.
      el.classList.add("ring-2", "ring-[#7A5200]", "ring-offset-2");
      setTimeout(() => el.classList.remove("ring-2", "ring-[#7A5200]", "ring-offset-2"), 1800);
    }
  };

  return (
    <div className="relative mb-6 rounded-xl overflow-hidden border border-[#7A5200]/30 bg-gradient-to-r from-[#FBE9E1] via-[#FDF5EA] to-[#FBE9E1]" data-testid="byok-promo-banner">
      <div className="relative px-5 py-4 flex items-start gap-4 flex-wrap">
        <div className="w-10 h-10 rounded-md bg-[#7A5200] text-[#FDFCF8] flex items-center justify-center shrink-0">
          <Sparkle size={20} weight="duotone" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#7A5200] font-medium">Save up to 80% on Claude + GPT usage</div>
          <h3 className="font-serif-display text-xl text-[#1C1B1A] mt-1 leading-tight">
            Bring your own Anthropic + OpenAI keys
          </h3>
          <p className="text-sm text-[#6B6A68] mt-1.5 leading-relaxed max-w-2xl">
            Your Claude and GPT calls currently route through the platform reservoir at a 3× markup. Connect your own provider accounts and pay only the 1× raw invoice — your voucher tracker turns <span className="text-[#2F6B3A] font-medium">green</span> and your power-creator costs drop by ~67%.
          </p>
          <button onClick={scrollToBYOK}
            data-testid="byok-promo-cta"
            className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-[#1C1B1A] text-[#FDFCF8] text-xs uppercase tracking-[0.15em] hover:bg-[#3A4A3E] transition-colors">
            Set up direct connection <ArrowRight size={12} weight="bold" />
          </button>
        </div>
        <button onClick={dismiss}
          data-testid="byok-promo-dismiss"
          className="text-[#6B6A68] hover:text-[#1C1B1A] p-1 -mt-1 -mr-1"
          title="Dismiss for this session">
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
